import { MGPMap, MGPOptional } from '@everyboard/lib';
import { CoordSet } from '../../jscaip/CoordSet';
import { FourStatePiece } from '../../jscaip/FourStatePiece';
import { PieceThreat } from '../../jscaip/PieceThreat';
import { Player } from '../../jscaip/Player';
import { PlayerNumberTable } from '../../jscaip/PlayerNumberTable';
import { Vector } from '../../jscaip/Vector';
import { TriangularCheckerBoard } from '../../jscaip/state/TriangularCheckerBoard';
import { CoerceoHeuristic } from './CoerceoHeuristic';
import { CoerceoStep } from './CoerceoMove';
import { CoerceoState } from './CoerceoState';
export class CoerceoPiecesThreatsTilesHeuristic extends CoerceoHeuristic {
    getMetrics(node, _config) {
        const state = node.gameState;
        const pieceMap = this.getPiecesMap(state);
        const threatMap = this.getThreatMap(state, pieceMap);
        const filteredThreatMap = this.filterThreatMap(threatMap, state);
        const safeIndex = 0;
        const threatenedIndex = 1;
        const tilesIndex = 2;
        const metrics = PlayerNumberTable.of([0, 0, 0], [0, 0, 0]);
        for (const owner of Player.PLAYERS) {
            for (const coord of pieceMap.get(owner).get()) {
                if (filteredThreatMap.get(coord).isPresent()) {
                    metrics.add(owner, threatenedIndex, 1);
                }
                else {
                    metrics.add(owner, safeIndex, 1);
                }
            }
            metrics.add(owner, tilesIndex, state.tiles.get(owner));
        }
        return metrics;
    }
    getThreatMap(state, pieces) {
        const threatMap = new MGPMap();
        for (const player of Player.PLAYERS) {
            for (const piece of pieces.get(player).get()) {
                const threat = this.getThreat(piece, state);
                if (threat.isPresent()) {
                    threatMap.set(piece, threat.get());
                }
            }
        }
        return threatMap;
    }
    getThreat(coord, state) {
        const directThreatInfo = this.getDirectThreats(coord, state);
        let directThreats = directThreatInfo.directThreats;
        const uniqueFreedom = directThreatInfo.uniqueFreedom;
        const emptiableNeighborTile = directThreatInfo.emptiableNeighborTile;
        if (uniqueFreedom.isPresent()) {
            const movingThreats = [];
            for (const step of CoerceoStep.STEPS) {
                const movingThreat = uniqueFreedom.get().getNext(step.direction, 1);
                if (this.isMovingThreat(coord, movingThreat, state, directThreats)) {
                    movingThreats.push(coord);
                }
            }
            if (movingThreats.length > 0) {
                const pieceThreat = new PieceThreat(new CoordSet(directThreats), new CoordSet(movingThreats));
                return MGPOptional.of(pieceThreat);
            }
        }
        if (emptiableNeighborTile.isPresent()) {
            directThreats = directThreats.filter((c) => c.equals(emptiableNeighborTile.get()));
            const directThreatsSet = new CoordSet(directThreats);
            const pieceThreat = new PieceThreat(directThreatsSet, new CoordSet([emptiableNeighborTile.get()]));
            return MGPOptional.of(pieceThreat);
        }
        return MGPOptional.empty();
    }
    getDirectThreats(coord, state) {
        let uniqueFreedom = MGPOptional.empty();
        let emptiableNeighborTile = MGPOptional.empty();
        const threatenerPlayer = state.getPieceAt(coord).getPlayer();
        const opponent = threatenerPlayer.getOpponent();
        const directThreats = [];
        const neighbors = TriangularCheckerBoard
            .getNeighbors(coord)
            .filter((c) => state.isOnBoard(c));
        for (const directThreat of neighbors) {
            const threat = state.getPieceAt(directThreat);
            if (threat.is(opponent)) {
                directThreats.push(directThreat);
                if (this.tileCouldBeRemovedThisTurn(directThreat, state, opponent)) {
                    emptiableNeighborTile = MGPOptional.of(directThreat);
                }
            }
            else if (threat === FourStatePiece.EMPTY) {
                if (uniqueFreedom.isPresent()) {
                    // more than one freedom!
                    return { directThreats: [], emptiableNeighborTile, uniqueFreedom: MGPOptional.empty() };
                }
                else {
                    uniqueFreedom = MGPOptional.of(directThreat);
                }
            }
        }
        return { directThreats, emptiableNeighborTile, uniqueFreedom };
    }
    isMovingThreat(coord, movingThreat, state, directThreats) {
        const threatenerPlayer = state.getPieceAt(coord).getPlayer();
        const opponent = threatenerPlayer.getOpponent();
        return state.hasPieceBelongingTo(movingThreat, opponent) &&
            directThreats.some((c) => c.equals(movingThreat)) === false;
    }
    tileCouldBeRemovedThisTurn(coord, state, OPPONENT) {
        const player = OPPONENT.getOpponent();
        const isTileRemovable = state.isDeconnectable(coord);
        if (isTileRemovable === false) {
            return false;
        }
        let uniqueThreat = MGPOptional.empty();
        // for all coord of the tiles
        const tileUpperLeft = CoerceoState.getTilesUpperLeftCoord(coord);
        for (let tileY = 0; tileY < 2; tileY++) {
            for (let tileX = 0; tileX < 3; tileX++) {
                const tileCoord = tileUpperLeft.getNext(new Vector(tileX, tileY), 1);
                if (state.getPieceAt(tileCoord).is(OPPONENT)) {
                    if (this.pieceCouldLeaveTheTile(tileCoord, state)) {
                        // Then add it to the threat list
                        uniqueThreat = MGPOptional.of(tileCoord);
                    }
                    else {
                        return false;
                    }
                }
                else if (state.getPieceAt(tileCoord).is(player)) {
                    return false;
                }
            }
        }
        return uniqueThreat.isPresent();
    }
    pieceCouldLeaveTheTile(piece, state) {
        const startingTileUpperLeft = CoerceoState.getTilesUpperLeftCoord(piece);
        for (const dir of CoerceoStep.STEPS) {
            const landing = piece.getNext(dir.direction, 1);
            const landingTileUpperLeft = CoerceoState.getTilesUpperLeftCoord(landing);
            if (startingTileUpperLeft.equals(landingTileUpperLeft) === false &&
                state.hasPieceAt(landing, FourStatePiece.EMPTY)) {
                return true;
            }
        }
        return false;
    }
    filterThreatMap(threatMap, state) {
        const filteredThreatMap = new MGPMap();
        const threateneds = threatMap.getKeyList();
        const threatenedPlayerPieces = threateneds.filter((coord) => {
            return state.getPieceAt(coord).is(state.getCurrentPlayer());
        });
        const threatenedOpponentPieces = new CoordSet(threateneds.filter((coord) => {
            return state.getPieceAt(coord).is(state.getCurrentOpponent());
        }));
        for (const threatenedPiece of threatenedPlayerPieces) {
            const oldThreat = threatMap.get(threatenedPiece).get();
            let newThreat = MGPOptional.empty();
            for (const directOldThreat of oldThreat.directThreats) {
                if (threatenedOpponentPieces.contains(directOldThreat) === false) {
                    // if the direct threat of this piece is not a false threat
                    const newMover = [];
                    for (const mover of oldThreat.mover) {
                        if (threatenedOpponentPieces.contains(mover) === false) {
                            // if the moving threat of this piece is real
                            newMover.push(mover);
                        }
                    }
                    if (newMover.length > 0) {
                        const pieceThreat = new PieceThreat(oldThreat.directThreats, new CoordSet(newMover));
                        newThreat = MGPOptional.of(pieceThreat);
                    }
                }
            }
            if (newThreat.isPresent()) {
                filteredThreatMap.set(threatenedPiece, newThreat.get());
            }
        }
        for (const threatenedOpponentPiece of threatenedOpponentPieces) {
            const threatSet = threatMap.get(threatenedOpponentPiece).get();
            filteredThreatMap.set(threatenedOpponentPiece, threatSet);
        }
        return filteredThreatMap;
    }
}
